<template>
  <div class="columns is-multiline">
    <div class="column">
      <div class="columns">
        <div class="column" />
        <div class="column">
          <div class="subtitle">{{ category.name }}
            <span v-if="isSystemCategory"
              class="icon tooltip"
              data-tooltip="System category. Integrations resolve it by name, so it cannot be renamed or deleted. Documents an integration files here are view only.">
              <i class="mdi mdi-lock mdi-18px" />
            </span>
            <span v-if="attachments.length > 0"
              class="has-badge-rounded has-badge-info has-badge-large pl-3"
              :data-badge="attachments.length" />
          </div>
        </div>
        <div class="column is-narrow">
          <p class="buttons">
            <a v-show="hasSelected"
              class="button tooltip"
              @click="openEmailModal()"
              data-tooltip="Email">
              <span class="icon">
                <i class="mdi mdi-email-fast mdi-24px" />
              </span>
            </a>
            <agent-scan-button @scan-results="(e)=>handleScanResult(e)" />
            <a class="button tooltip"
              @click="$emit('image-uploaded')"
              data-tooltip="Refresh">
              <span class="icon">
                <i class="mdi mdi-refresh mdi-24px" />
              </span>
            </a>
            <a class="button tooltip"
              @click="setSelectedStatusAll(true)"
              data-tooltip="Select all">
              <span class="icon has-text-success">
                <i class="mdi mdi-select-all mdi-24px" />
              </span>
            </a>
            <a v-show="hasSelected"
              class="button tooltip"
              @click="setSelectedStatusAll(false)"
              data-tooltip="Deselect all">
              <span class="icon has-text-grey">
                <i class="mdi mdi-select-off mdi-24px" />
              </span>
            </a>
            <a v-show="hasSelectedUnlocked"
              class="button tooltip"
              @click="toggleCopyAttachmentModal(true)"
              data-tooltip="Copy to quote">
              <span class="icon has-text-primary">
                <i class="mdi mdi-content-copy mdi-24px" />
              </span>
            </a>
            <a v-show="hasSelectedUnlocked"
              class="button tooltip"
              @click="showDeleteAllModal"
              data-tooltip="Delete selected">
              <span class="icon has-text-danger">
                <i class="mdi mdi-delete-sweep mdi-24px" />
              </span>
            </a>
            <confirm-modal :active.sync="isDeleteAllModalActive"
              @ok="deleteSelectedAttachments(true)"
              @cancel="deleteSelectedAttachments(false)"
              :ok-text="'Yes'"
              :cancel-text="'No'">
              <p slot="text-title">Delete attachments</p>
              <p slot="text-content">
                <span class="has-text-primary has-text-weight-bold">{{ selectedUnlockedAttachments.length }}</span> selected attachments will be deleted. Continue?
              </p>
              <p v-if="selectedLockedCount > 0"
                slot="text-content">
                {{ selectedLockedCount }} locked document(s) attached by an integration will be kept.
              </p>
            </confirm-modal>
            <a v-show="hasSelected"
              class="button tooltip"
              @click="downloadSelectedAttachments()"
              data-tooltip="Download selected">
              <span class="icon has-text-primary">
                <i class="mdi mdi-download mdi-24px" />
              </span>
            </a>
          </p>
        </div>

      </div>
      <div>
        <draggable :list="attachments"
          class="columns is-multiline"
          group="attachments"
          :move="canMoveAttachment"
          @change="(event)=>attachmentChange(category,event)">

          <div v-for="attachment in attachments"
            class="column is-1 m-0 p-1"
            :key="attachment.quoteAttachmentId">
            <QuoteAttachment :attachment="attachment"
              @update:attachment="(e)=>handleAttachmentChange(e,attachment)"
              :quote-id="quoteId"
              :quote-attachment-category-id="category.quoteAttachmentCategoryId"
              :quote-attachment-id="attachment.quoteAttachmentId"
              @image-uploaded="imageUpdated()"
              :categories="categories" />
          </div>
          <div class="column is-3 m-0 p-1">
            <file-drop-zone :quote-id="quoteId"
              style="height: 100%"
              :default-size="128"
              :quote-attachment-category-id="category.quoteAttachmentCategoryId"
              :max-file-size-mb="maxFileSizeMb"
              @image-uploaded="imageUpdated()"
              additional-classes="quote-attachment-dropzone" />
          </div>
        </draggable>
      </div>
    </div>
    <vue-email-modal :active.sync="isEmailModalActive"
      icon-name="mdi-email-open"
      icon-type="primary"
      @send="sendEmail"
      @discard="discardEmail"
      @attach="attachMedia"
      @show-attach="false"
      :enable-send="enableSend && !isImageAttachActive"
      :enable-attach="false"
      :toggle-attach-button="false">
      <div slot="text-content">
        <vue-email v-if="isEmailModalActive"
          v-show="!isImageAttachActive"
          ref="emailer"
          v-model="emailer"
          :quote-attachments="allSelectedAttachments"
          @new-email="true"
          @sent="sent"
          :asset-id="emailer?.assetId" />
        <image-select v-if="emailer && isImageAttachActive"
          v-show="isImageAttachActive"
          v-model="selectedImages"
          :quote-id="emailer.assetId"
          :quote-ids="quoteIds" />
      </div>
    </vue-email-modal>
    <quote-no-search-modal v-if="isCopyImageModalActive"
      :active.sync="isCopyImageModalActive"
      @copy="copyQuoteAttachmentToOtherQuote($event)"
      @close="toggleCopyAttachmentModal(false)" />
  </div>
</template>

<script>
import QuoteAttachment from './QuoteAttachment.vue'
import FileDropZone from '@/components/FileDropZone.vue'
import QuoteService from '@/views/quote/QuoteService'
import AgentService from '@/services/AgentService'
import draggable from 'vuedraggable'
import QuoteNoSearchModal from './QuoteNoSearchModal.vue'
import { VueEmail, VueEmailModal } from '@/components/VueEmail'
import { Emailer } from '@/classes'
import StoreUtil from '@/store/utils'
import _cloneDeep from 'lodash/cloneDeep'
import StoreMixin from '@/components/printpreview/storeMixin.js'
import AgentScanButton from '@/components/Agent/AgentScanButton'
import { DateTime } from 'luxon'

import ConfirmModal from '@/components/BulmaModal/ConfirmModal.vue'

const divider = '=============================================================='
const copyright =
  'All accompanying materials are the exclusive property of [COMPANY].  All [COMPANY] rights are reserved.  These Materials must not be reproduced, adapted, modified, published, or communicated to any person in any form, without express prior written consent of [COMPANY].  Any unauthorised use of these Materials will be vigorously pursued and may result in legal action, including a claim for damages or an account for loss of profits.'

export default {
  name: 'QuoteAttachmentCategory',
  components: {
    QuoteAttachment,
    FileDropZone,
    draggable,
    QuoteNoSearchModal,
    VueEmail,
    VueEmailModal,
    AgentScanButton,
    ConfirmModal
  },
  mixins: [StoreMixin],
  props: {
    quoteId: {
      type: String
    },
    category: {
      type: Object,
      default: () => {}
    },
    attachments: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      default: () => []
    },
    maxFileSizeMb: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      isCopyImageModalActive: false,
      isEmailModalActive: false,
      isImageAttachActive: false,
      emailer: null,
      queuedFileCount: 0, // Track uploaded attachments
      isDeleteAllModalActive: false
    }
  },
  computed: {
    isSystemCategory() {
      return this.category?.isSystem === true
    },
    allSelectedAttachments() {
      let set = []
      for (let category of this.categories) {
        for (let attachment of category?.attachments || []) {
          if (!attachment.isSelected) {
            continue
          }

          set.push(attachment)
        }
      }
      return set
    },
    selectedAttachments() {
      return this.attachments.filter((row) => row.isSelected)
    },
    hasSelected() {
      return this.selectedAttachments.length > 0
    },
    // Locked documents (filed by an integration) can be viewed, downloaded and emailed, but not
    // deleted, moved or copied to another quote - those actions only apply to the rest.
    selectedUnlockedAttachments() {
      return this.selectedAttachments.filter((row) => !row.isLocked)
    },
    selectedLockedCount() {
      return this.selectedAttachments.length - this.selectedUnlockedAttachments.length
    },
    hasSelectedUnlocked() {
      return this.selectedUnlockedAttachments.length > 0
    },
    enableSend() {
      return (this.emailer?.toEmails?.length || 0) > 0 && !this.isEnteringNewEmail
    }
  },
  created() {
    this.clearSessionStorage()
  },
  mounted() {
    this.resetEmailer()
  },
  methods: {
    handleAttachmentChange(e, attachment) {
      this.$emit('update:attachments', { category: this.category, ...e })
    },
    async handleScanResult(result) {
      let packet = (await QuoteService.getQuoteAttachmentUploadUrl(this.quoteId)).data
      let uploadURL = packet.url
      let guid = packet.guid

      let dateTimeStamp = DateTime.now().toFormat('yyyyMMddHHmmss')
      let fileName = 'Scanned' + dateTimeStamp + '.png'

      const base64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
        const byteCharacters = atob(b64Data)
        const byteArrays = []

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
          const slice = byteCharacters.slice(offset, offset + sliceSize)

          const byteNumbers = new Array(slice.length)
          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i)
          }

          const byteArray = new Uint8Array(byteNumbers)
          byteArrays.push(byteArray)
        }

        const blob = new Blob(byteArrays, { type: contentType })
        return blob
      }

      await AgentService.Scanner.uploadFile(uploadURL, base64toBlob(result, 'image/png'))
      await QuoteService.setupQuoteAttachment(this.quoteId, this.category.quoteAttachmentCategoryId, guid, fileName)
      this.$emit('image-uploaded')
      this.queuedFileCount++
    },
    async sent() {
      this.discardEmail()
    },
    attachMedia() {},
    async sendEmail() {
      this.$refs.emailer.send()
    },
    discardEmail() {
      this.isEmailModalActive = false
      this.isImageAttachActive = false
      this.resetEmailer()
    },
    async resetEmailer() {
      const sessionEmailer = this.retrieveEmailer() || StoreUtil.getLocalStorage(this.$route.query.emailerId, 'emailer')
      if (sessionEmailer) {
        this.emailer = sessionEmailer
      } else {
        this.emailer = _cloneDeep(this.getEmailer(this.$route.query.emailerId) || new Emailer())
      }
      this.emailer.message = `\n\n${divider}\n${copyright.replace(/\[COMPANY\]/g, this.$userInfo.companyName)}`
      StoreUtil.removeLocalStorage(this.$route.query.emailerId, 'emailer')
      this.persistEmailer()
      this.updateAttachmentList()
    },
    updateAttachmentList() {
      // PCK-213 - hand the composer sized objects on quoteAttachments, as PrintPreview does,
      // rather than bare filenames on attachments. Filenames counted as zero (totalAttachmentSize
      // skips strings) so the meter read 0 B, and removing a chip did not change what was sent
      // because the ids came from the allSelectedAttachments prop instead of the emailer.
      // attachments stays empty: entries in both lists would double-count in the total.
      this.emailer.attachments = []
      this.$set(
        this.emailer,
        'quoteAttachments',
        this.allSelectedAttachments.map((a) => ({
          quoteAttachmentId: a.quoteAttachmentId,
          fileName: a.filename,
          fileSize: a.fileSize
        }))
      )
    },
    retrieveEmailer() {
      return JSON.parse(sessionStorage.getItem(`${this.$userInfo.sessionId}|${this.$route.meta.ekey}`))
    },
    persistEmailer() {
      sessionStorage.setItem(`${this.$userInfo.sessionId}|${this.$route.meta.ekey}`, JSON.stringify(this.emailer))
    },
    clearSessionStorage() {
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|${this.$route.meta.ekey}`)
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|${this.$route.meta.pkey}`)
    },
    async copyQuoteAttachmentToOtherQuote(targetQuoteId) {
      const rows = this.selectedUnlockedAttachments
      for (let row of rows) {
        await QuoteService.copyQuoteAttachmentToQuote(this.quoteId, row.quoteAttachmentId, targetQuoteId, this.category.quoteAttachmentCategoryId, false)
      }
      this.toggleCopyAttachmentModal(false)
      let message = `${rows.length} attachments copied`
      if (this.selectedLockedCount > 0) {
        message += `. ${this.selectedLockedCount} locked document(s) attached by an integration were not copied.`
      }
      this.$notification.openNotificationWithType('success', 'Copy attachments', message)
    },
    toggleCopyAttachmentModal(value) {
      this.isCopyImageModalActive = value
    },
    async getCompiledAttachmentDataUrl(attachment) {
      return attachment.externalStorageURL
    },

    async downloadSelectedAttachmentsAsZip() {
      // Download all selected attachments (doc/image/pdf) in a zip file
      const JSZip = (await import('jszip')).default
      const FileSaver = (await import('file-saver')).default
      const zip = new JSZip()
      const attachments = this.selectedAttachments
      const fetchFile = async (url) => {
        const response = await fetch(url)
        return await response.blob()
      }
      for (const attachment of attachments) {
        const url = attachment.externalStorageURL
        if (url) {
          const blob = await fetchFile(url)
          zip.file(attachment.filename, blob)
        }
      }
      const content = await zip.generateAsync({ type: 'blob' })
      FileSaver.saveAs(content, 'attachments.zip')
    },
    async downloadSelectedAttachments() {
      this.$showSpinner()
      await this.downloadSelectedAttachmentsAsZip()
      this.$hideSpinner()
    },
    // Reordering a locked document within its own category is fine; dragging it into another
    // category is not.
    canMoveAttachment(event) {
      return !(event.draggedContext?.element?.isLocked && event.from !== event.to)
    },
    async attachmentChange(targetCategory, event) {
      if (event.moved !== undefined) {
        for (let i = 0; i < this.attachments.length; i++) {
          this.attachments[i].orderIndex = i
          this.$emit('update:attachments', { category: this.category, attachment: this.attachments[i], isSelected: this.attachments[i].isSelected })
          QuoteService.updateQuoteAttachmentOrderIndex(this.attachments[i].quoteAttachmentId, i)
        }
      }

      if (event.added !== undefined) {
        // Belt and braces: canMoveAttachment already blocks this, and the server rejects it with a 409.
        if (event.added.element?.isLocked) {
          this.$emit('image-uploaded')
          return
        }

        var movedItem = event.added.element
        await QuoteService.copyQuoteAttachment(this.quoteId, movedItem.quoteAttachmentId, targetCategory.quoteAttachmentCategoryId, true)
        this.$emit('image-uploaded')
      }
    },
    showDeleteAllModal() {
      this.isDeleteAllModalActive = true
    },
    async deleteSelectedAttachments(confirm) {
      this.isDeleteAllModalActive = false
      if (confirm) {
        const rows = this.selectedUnlockedAttachments
        for (var row of rows) {
          await QuoteService.deleteQuoteAttachment(row.quoteAttachmentId)
        }
        this.$notification.openNotificationWithType('success', 'Delete attachments', `${rows.length} attachments deleted`)
        this.$emit('image-uploaded')
      }
    },
    // Removed duplicate 'attachments' key to fix vue/no-dupe-keys lint error
    setSelectedStatusAll(value) {
      for (var row of this.attachments) {
        row.isSelected = value // update local state for immediate UI feedback
        this.$emit('update:attachments', { attachment: row, category: this.category, isSelected: value })
      }
    },
    imageUpdated() {
      this.queuedFileCount++
      this.$emit('image-uploaded')
    },
    openEmailModal() {
      this.updateAttachmentList()
      this.isEmailModalActive = true
    }
  }
}
</script>
<style>
.quote-attachment-dropzone {
  height: 100%;
  padding: 0px !important;
  min-height: 100% !important;
}

.quote-attachment-dropzone .dz-message {
  margin: 0 !important;
  margin-top: 10px !important;
}

.quote-attachment-dropzone .dz-preview.dz-file-preview {
  width: 128px !important;
  height: 128px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 16px !important;
  border: 1px solid #e0e0e0 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  margin: 8px !important;
  background-color: #ffffff !important;
  position: relative !important;
  overflow: visible !important;
}

.quote-attachment-dropzone .dz-preview.dz-file-preview .dz-image {
  border-radius: 14px;
  overflow: hidden;
  width: 100% !important;
  height: 100% !important;
  position: relative;
  display: block;
  z-index: 10;
}

.quote-attachment-dropzone .dz-preview.dz-file-preview .dz-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.quote-attachment-dropzone .dz-preview.dz-error {
  border-color: #d9534d !important;
}

.quote-attachment-dropzone .dz-preview.dz-success {
  border-color: #5cb85c !important;
  display: none !important;
}

.quote-attachment-dropzone .dz-preview .dz-remove {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 30;
  width: 22px;
  height: 22px;
  background-color: #ffffff;
  border: 1px solid #666666;
  border-radius: 50%;
  color: #4a4a4a;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  transition: all 0.2s ease;
}

.quote-attachment-dropzone .dz-preview .dz-remove:hover {
  background-color: #f8f8f8;
  border-color: #555555;
  color: #333333;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
</style>